import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Pencil, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const emptyNews = { title: "", slug: "", excerpt: "", body_html: "", cover_image: "", is_published: true };

export default function AdminContent() {
  const { settings, refresh } = useSiteSettings();
  const [tab, setTab] = useState("settings"); // settings | news
  const [news, setNews] = useState([]);
  const [newsOpen, setNewsOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [newsForm, setNewsForm] = useState(emptyNews);
  const [settingsForm, setSettingsForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setSettingsForm({
        logo_url: settings.logo_url || "",
        favicon_url: settings.favicon_url || "",
        site_url: settings.site_url || "",
        email_notifications_enabled: settings.email_notifications_enabled !== false,
        seo: {
          title: "", description: "", keywords: "", og_image: "",
          ...(settings.seo || {}),
        },
        announcement: { active: false, text: "", cta_text: "", cta_link: "", ...(settings.announcement || {}) },
        popup: { active: false, title: "", body: "", cta_text: "", cta_link: "", ...(settings.popup || {}) },
        contact: { whatsapp: "", whatsapp_prefill: "", phone: "", email: "", ...(settings.contact || {}) },
        social: { facebook: "", instagram: "", youtube: "", twitter: "", linkedin: "", ...(settings.social || {}) },
        tawk_enabled: settings.tawk_enabled ?? true,
      });
    }
  }, [settings]);

  const uploadBranding = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/site-settings/branding-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSettingsForm((f) => ({ ...f, [`${kind}_url`]: data.url }));
      await refresh();
      toast.success(`${kind === "logo" ? "Logo" : "Favicon"} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const loadNews = () => api.get("/admin/news").then((r) => setNews(r.data.news));
  useEffect(() => { loadNews(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-settings", settingsForm);
      await refresh();
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openNewNews = () => { setEditingNews(null); setNewsForm(emptyNews); setNewsOpen(true); };
  const openEditNews = (n) => { setEditingNews(n.id); setNewsForm({ ...n }); setNewsOpen(true); };

  const saveNews = async (e) => {
    e.preventDefault();
    try {
      if (editingNews) {
        await api.put(`/admin/news/${editingNews}`, newsForm);
        toast.success("Article updated");
      } else {
        await api.post("/admin/news", newsForm);
        toast.success("Article created");
      }
      setNewsOpen(false);
      await loadNews();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const deleteNews = async (n) => {
    if (!window.confirm(`Delete "${n.title}"?`)) return;
    try {
      await api.delete(`/admin/news/${n.id}`);
      toast.success("Deleted");
      await loadNews();
    } catch { toast.error("Delete failed"); }
  };

  if (!settingsForm) return <DashboardLayout admin><div className="text-slate-500">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout admin>
      <div className="mb-6">
        <div className="section-label">Admin</div>
        <h1 className="mt-1 font-display font-bold text-3xl text-[#0A192F]">Content &amp; Marketing</h1>
        <p className="text-slate-600 mt-1 text-sm">Manage the announcement bar, popup, social links, and news posts.</p>
      </div>

      <div className="mb-5 inline-flex rounded-lg bg-slate-100 p-1">
        {["settings", "seo", "news", "pages"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-md text-sm font-medium capitalize ${tab === t ? "bg-white shadow text-[#0A192F]" : "text-slate-600"}`}
            data-testid={`content-tab-${t}`}
          >
            {t === "settings" ? "Site Settings" : t === "seo" ? "SEO & Sitemap" : t === "news" ? "News Posts" : "Pages"}
          </button>
        ))}
      </div>

      {tab === "settings" && (
        <div className="space-y-5">
          {/* Logo */}
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-logo-panel">
            <div className="section-label">Site logo</div>
            <div className="font-display font-semibold text-lg text-[#0A192F] mt-1 mb-3">Header &amp; footer branding</div>
            <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-center">
              <div>
                <Label>Upload logo</Label>
                <div className="mt-1 flex items-center gap-3">
                  <input
                    id="logo-upload-input"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                    className="hidden"
                    onChange={(e) => uploadBranding("logo", e.target.files?.[0])}
                    data-testid="logo-upload-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById("logo-upload-input")?.click()}
                    data-testid="logo-upload-btn"
                  >
                    Choose file
                  </Button>
                  {settingsForm.logo_url && (
                    <button
                      type="button"
                      onClick={() => setSettingsForm({ ...settingsForm, logo_url: "" })}
                      className="text-xs text-red-600 hover:underline"
                      data-testid="logo-clear"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-2">PNG, SVG, JPG or WebP · max 2 MB · transparent PNG works best. Leave empty to keep the "W2" default mark.</div>
              </div>
              {settingsForm.logo_url && (
                <div className="p-3 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center h-16 min-w-[120px]" data-testid="logo-preview-wrap">
                  <img src={settingsForm.logo_url} alt="preview" className="h-10 object-contain" onError={(e) => { e.target.style.display = "none"; }} data-testid="logo-preview-img" />
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="section-label">Email notifications</div>
                <div className="text-sm text-slate-600 mt-1">Send transactional emails from <b>support@way2efiling.com</b> for registration, payment received, status updates, deliverable uploads, and document rejections.</div>
              </div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-[#1E3A8A]"
                  checked={settingsForm.email_notifications_enabled !== false}
                  onChange={(e) => setSettingsForm({ ...settingsForm, email_notifications_enabled: e.target.checked })}
                  data-testid="email-notifications-toggle"
                />
                <span>{settingsForm.email_notifications_enabled !== false ? "Enabled" : "Disabled"}</span>
              </label>
            </div>
          </div>

          {/* Favicon */}
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-favicon-panel">
            <div className="section-label">Site icon (Favicon)</div>
            <div className="font-display font-semibold text-lg text-[#0A192F] mt-1 mb-3">Browser tab icon</div>
            <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-center">
              <div>
                <Label>Upload favicon</Label>
                <div className="mt-1 flex items-center gap-3">
                  <input
                    id="favicon-upload-input"
                    type="file"
                    accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => uploadBranding("favicon", e.target.files?.[0])}
                    data-testid="favicon-upload-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById("favicon-upload-input")?.click()}
                    data-testid="favicon-upload-btn"
                  >
                    Choose file
                  </Button>
                  {settingsForm.favicon_url && (
                    <button
                      type="button"
                      onClick={() => setSettingsForm({ ...settingsForm, favicon_url: "" })}
                      className="text-xs text-red-600 hover:underline"
                      data-testid="favicon-clear"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-2">Square image · 32×32 or larger · PNG/SVG/ICO · max 2 MB. Applied as the browser tab icon and iOS home-screen icon.</div>
              </div>
              {settingsForm.favicon_url && (
                <div className="p-3 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center h-16 w-16" data-testid="favicon-preview-wrap">
                  <img src={settingsForm.favicon_url} alt="favicon preview" className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display = "none"; }} data-testid="favicon-preview-img" />
                </div>
              )}
            </div>
          </div>

          {/* Announcement */}
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-ann-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="section-label">Announcement bar</div>
                <div className="font-display font-semibold text-lg text-[#0A192F] mt-1">Top-of-site banner</div>
              </div>
              <Switch checked={settingsForm.announcement.active} onCheckedChange={(v) => setSettingsForm({ ...settingsForm, announcement: { ...settingsForm.announcement, active: v } })} data-testid="ann-active-toggle" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Text</Label>
                <Input value={settingsForm.announcement.text} onChange={(e) => setSettingsForm({ ...settingsForm, announcement: { ...settingsForm.announcement, text: e.target.value } })} data-testid="ann-text" />
              </div>
              <div>
                <Label>CTA Text</Label>
                <Input value={settingsForm.announcement.cta_text} onChange={(e) => setSettingsForm({ ...settingsForm, announcement: { ...settingsForm.announcement, cta_text: e.target.value } })} />
              </div>
              <div>
                <Label>CTA Link</Label>
                <Input value={settingsForm.announcement.cta_link} onChange={(e) => setSettingsForm({ ...settingsForm, announcement: { ...settingsForm.announcement, cta_link: e.target.value } })} />
              </div>
            </div>
          </div>

          {/* Popup */}
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-popup-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="section-label">Homepage popup</div>
                <div className="font-display font-semibold text-lg text-[#0A192F] mt-1">Offer / announcement modal</div>
              </div>
              <Switch checked={settingsForm.popup.active} onCheckedChange={(v) => setSettingsForm({ ...settingsForm, popup: { ...settingsForm.popup, active: v } })} data-testid="popup-active-toggle" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Title</Label>
                <Input value={settingsForm.popup.title} onChange={(e) => setSettingsForm({ ...settingsForm, popup: { ...settingsForm.popup, title: e.target.value } })} data-testid="popup-title" />
              </div>
              <div>
                <Label>CTA Text</Label>
                <Input value={settingsForm.popup.cta_text} onChange={(e) => setSettingsForm({ ...settingsForm, popup: { ...settingsForm.popup, cta_text: e.target.value } })} />
              </div>
              <div className="sm:col-span-2">
                <Label>Body</Label>
                <Textarea rows={2} value={settingsForm.popup.body} onChange={(e) => setSettingsForm({ ...settingsForm, popup: { ...settingsForm.popup, body: e.target.value } })} />
              </div>
              <div className="sm:col-span-2">
                <Label>CTA Link</Label>
                <Input value={settingsForm.popup.cta_link} onChange={(e) => setSettingsForm({ ...settingsForm, popup: { ...settingsForm.popup, cta_link: e.target.value } })} />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="section-label">Contact</div>
            <div className="font-display font-semibold text-lg text-[#0A192F] mt-1 mb-3">WhatsApp / Support</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>WhatsApp number (with country code, no +)</Label>
                <Input value={settingsForm.contact.whatsapp} onChange={(e) => setSettingsForm({ ...settingsForm, contact: { ...settingsForm.contact, whatsapp: e.target.value.replace(/\D/g, "") } })} placeholder="918448440219" data-testid="contact-whatsapp" />
              </div>
              <div>
                <Label>WhatsApp prefilled message</Label>
                <Input value={settingsForm.contact.whatsapp_prefill} onChange={(e) => setSettingsForm({ ...settingsForm, contact: { ...settingsForm.contact, whatsapp_prefill: e.target.value } })} />
              </div>
              <div>
                <Label>Support phone</Label>
                <Input value={settingsForm.contact.phone} onChange={(e) => setSettingsForm({ ...settingsForm, contact: { ...settingsForm.contact, phone: e.target.value } })} />
              </div>
              <div>
                <Label>Support email</Label>
                <Input value={settingsForm.contact.email} onChange={(e) => setSettingsForm({ ...settingsForm, contact: { ...settingsForm.contact, email: e.target.value } })} />
              </div>
            </div>
          </div>

          {/* Social */}
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-social-panel">
            <div className="section-label">Social links</div>
            <div className="font-display font-semibold text-lg text-[#0A192F] mt-1 mb-3">Displayed in the footer</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {["facebook", "instagram", "youtube", "twitter", "linkedin"].map((k) => (
                <div key={k}>
                  <Label className="capitalize">{k}</Label>
                  <Input value={settingsForm.social[k] || ""} onChange={(e) => setSettingsForm({ ...settingsForm, social: { ...settingsForm.social, [k]: e.target.value } })} placeholder="https://…" data-testid={`social-${k}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Tawk */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="section-label">Live chat</div>
                <div className="font-display font-semibold text-lg text-[#0A192F] mt-1">Tawk.to widget</div>
                <div className="text-xs text-slate-500 mt-1">Loads on public and customer pages only.</div>
              </div>
              <Switch checked={settingsForm.tawk_enabled} onCheckedChange={(v) => setSettingsForm({ ...settingsForm, tawk_enabled: v })} data-testid="tawk-toggle" />
            </div>
          </div>

          <Button onClick={saveSettings} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="settings-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save all settings"}
          </Button>
        </div>
      )}

      {tab === "news" && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm text-slate-500">{news.length} article{news.length === 1 ? "" : "s"}</div>
            <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={openNewNews} data-testid="news-new-btn">
              <Plus className="w-4 h-4 mr-1" /> New article
            </Button>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            {news.length === 0 ? (
              <div className="p-10 text-center text-slate-500">No articles yet.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {news.map((n) => (
                  <li key={n.id} className="p-4 flex items-center justify-between gap-3 hover:bg-slate-50">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[#0A192F] truncate">{n.title}</span>
                        {n.is_published ? <span className="status-badge badge-success">Published</span> : <span className="status-badge badge-muted">Draft</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 font-mono">/{n.slug}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditNews(n)} data-testid={`news-edit-${n.slug}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="outline" size="sm" onClick={() => deleteNews(n)} data-testid={`news-delete-${n.slug}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Dialog open={newsOpen} onOpenChange={setNewsOpen}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingNews ? "Edit article" : "New article"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={saveNews} className="space-y-4" data-testid="news-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Title</Label><Input required value={newsForm.title} onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })} data-testid="news-form-title" /></div>
                  <div><Label>Slug (URL)</Label><Input required value={newsForm.slug} onChange={(e) => setNewsForm({ ...newsForm, slug: e.target.value })} placeholder="e.g. itr-filing-deadline-2026" data-testid="news-form-slug" /></div>
                </div>
                <div>
                  <Label>Excerpt (1-2 sentence summary)</Label>
                  <Textarea rows={2} value={newsForm.excerpt} onChange={(e) => setNewsForm({ ...newsForm, excerpt: e.target.value })} />
                </div>
                <div>
                  <Label>Cover image URL (optional)</Label>
                  <Input value={newsForm.cover_image} onChange={(e) => setNewsForm({ ...newsForm, cover_image: e.target.value })} placeholder="https://…" />
                </div>
                <div>
                  <Label>Body (HTML allowed)</Label>
                  <Textarea rows={10} value={newsForm.body_html} onChange={(e) => setNewsForm({ ...newsForm, body_html: e.target.value })} className="font-mono text-xs" data-testid="news-form-body" />
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={newsForm.is_published} onCheckedChange={(v) => setNewsForm({ ...newsForm, is_published: v })} data-testid="news-form-published" />
                  <span className="text-sm">Published</span>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setNewsOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="news-form-save-btn">Save</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {tab === "pages" && <PagesTab />}
      {tab === "seo" && <SEOTab settingsForm={settingsForm} setSettingsForm={setSettingsForm} saveSettings={saveSettings} saving={saving} />}
    </DashboardLayout>
  );
}

function SEOTab({ settingsForm, setSettingsForm, saveSettings, saving }) {
  const seo = settingsForm.seo || {};
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const sitemapUrl = `${backendUrl}/api/sitemap.xml`;
  const robotsUrl = `${backendUrl}/api/robots.txt`;

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-seo-panel">
        <div className="section-label">Global SEO</div>
        <div className="font-display font-semibold text-lg text-[#0A192F] mt-1 mb-3">Default meta tags for every page</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label>Site canonical URL</Label>
            <Input value={settingsForm.site_url || ""} onChange={(e) => setSettingsForm({ ...settingsForm, site_url: e.target.value })} placeholder="https://way2efiling.com" data-testid="seo-site-url" />
            <div className="text-xs text-slate-500 mt-1">Used for canonical + Open Graph URLs and sitemap.xml.</div>
          </div>
          <div className="sm:col-span-2">
            <Label>Default title</Label>
            <Input value={seo.title || ""} onChange={(e) => setSettingsForm({ ...settingsForm, seo: { ...seo, title: e.target.value } })} placeholder="Way2Efiling – Tax & License Filing Simplified" data-testid="seo-title" />
          </div>
          <div className="sm:col-span-2">
            <Label>Default meta description</Label>
            <Textarea rows={2} value={seo.description || ""} onChange={(e) => setSettingsForm({ ...settingsForm, seo: { ...seo, description: e.target.value } })} placeholder="File ITR, GST, Udyam and FSSAI with expert support…" data-testid="seo-description" />
          </div>
          <div className="sm:col-span-2">
            <Label>Meta keywords (comma-separated)</Label>
            <Input value={seo.keywords || ""} onChange={(e) => setSettingsForm({ ...settingsForm, seo: { ...seo, keywords: e.target.value } })} placeholder="ITR filing, GST registration, FSSAI, Udyam" data-testid="seo-keywords" />
          </div>
          <div className="sm:col-span-2">
            <Label>Open Graph image URL (social share preview)</Label>
            <Input value={seo.og_image || ""} onChange={(e) => setSettingsForm({ ...settingsForm, seo: { ...seo, og_image: e.target.value } })} placeholder="https://…/og-image.png" data-testid="seo-og-image" />
          </div>
        </div>
        <Button onClick={saveSettings} disabled={saving} className="mt-4 bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="seo-save-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save SEO settings"}
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-sitemap-panel">
        <div className="section-label">Sitemap</div>
        <div className="font-display font-semibold text-lg text-[#0A192F] mt-1 mb-3">Auto-generated XML sitemap</div>
        <p className="text-sm text-slate-600 mb-3">The sitemap regenerates automatically. It includes home, services, service detail pages, news articles and every published CMS page.</p>
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xs uppercase tracking-wider text-slate-500 w-24">Sitemap</div>
            <a href={sitemapUrl} target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] font-mono text-xs break-all hover:underline" data-testid="sitemap-url">{sitemapUrl}</a>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xs uppercase tracking-wider text-slate-500 w-24">Robots.txt</div>
            <a href={robotsUrl} target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] font-mono text-xs break-all hover:underline" data-testid="robots-url">{robotsUrl}</a>
          </div>
        </div>
        <div className="mt-4 p-3 rounded-md bg-blue-50 border border-blue-100 text-xs text-blue-900">
          <b>Submit to search engines:</b> paste the sitemap URL into Google Search Console → Sitemaps, and Bing Webmaster Tools → Sitemaps.
        </div>
      </div>
    </div>
  );
}

function PagesTab() {
  const [pages, setPages] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", slug: "", body_html: "", is_published: true, show_in_footer: false, show_in_menu: false, menu_order: 100, seo_title: "", seo_description: "" });

  const load = () => api.get("/admin/pages").then((r) => setPages(r.data.pages));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ title: "", slug: "", body_html: "", is_published: true, show_in_footer: false, show_in_menu: false, menu_order: 100, seo_title: "", seo_description: "" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...p, show_in_menu: p.show_in_menu || false, menu_order: p.menu_order || 100, seo_title: p.seo_title || "", seo_description: p.seo_description || "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/pages/${editing}`, form);
        toast.success("Page updated");
      } else {
        await api.post("/admin/pages", form);
        toast.success("Page created");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete page "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/pages/${p.id}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-slate-500">{pages.length} page{pages.length === 1 ? "" : "s"}</div>
        <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={openNew} data-testid="page-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New page
        </Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {pages.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No pages yet. Create Privacy Policy, Terms, Refund Policy…</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {pages.map((p) => (
              <li key={p.id} className="p-4 flex items-center justify-between gap-3 hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[#0A192F] truncate">{p.title}</span>
                    {p.is_published ? <span className="status-badge badge-success">Published</span> : <span className="status-badge badge-muted">Draft</span>}
                    {p.show_in_footer && <span className="status-badge badge-info">Footer</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono">/pages/{p.slug}</div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`page-edit-${p.slug}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="outline" size="sm" onClick={() => del(p)} data-testid={`page-delete-${p.slug}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit page" : "New page"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="page-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="page-form-title" /></div>
              <div><Label>Slug</Label><Input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="privacy-policy" data-testid="page-form-slug" /></div>
            </div>
            <div>
              <Label>Body (HTML allowed)</Label>
              <Textarea rows={12} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} className="font-mono text-xs" data-testid="page-form-body" />
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
              <div className="section-label">Per-page SEO (optional)</div>
              <div>
                <Label>SEO title</Label>
                <Input value={form.seo_title || ""} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} placeholder="Overrides the default title" data-testid="page-form-seo-title" />
              </div>
              <div>
                <Label>SEO meta description</Label>
                <Textarea rows={2} value={form.seo_description || ""} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} placeholder="Shown in search results" data-testid="page-form-seo-desc" />
              </div>
            </div>
            <div className="flex items-center gap-6 flex-wrap">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="page-form-published" /> Published</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.show_in_footer} onCheckedChange={(v) => setForm({ ...form, show_in_footer: v })} data-testid="page-form-footer" /> Show in footer</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.show_in_menu} onCheckedChange={(v) => setForm({ ...form, show_in_menu: v })} data-testid="page-form-menu" /> Show in main menu</label>
              <div className="flex items-center gap-2 text-sm">
                <Label className="whitespace-nowrap m-0">Menu order</Label>
                <Input type="number" className="w-20" value={form.menu_order || 100} onChange={(e) => setForm({ ...form, menu_order: parseInt(e.target.value) || 100 })} data-testid="page-form-menu-order" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="page-form-save-btn">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
